(ns app.front.view.application.action-parameters
  "Implement custom modal content to require before sending a primary/secondary signal.

  `parameter-view` defines the UI, `valid?` checks whether the parameters are good enough
  to actually send the signal to the backend."
  (:require [app.front.ui :as ui]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.util :as futil]
            [app.front.view.appeals.set-appealed-modal :as set-appealed-modal]
            [app.front.view.conditions.common :as condition-common]
            [app.front.view.occupancy.edit :as partial-occupancy]
            [app.front.view.workspace.add-related :as add-related]
            [app.front.view.workspace.archival-on-finished :as archival-on-finished]
            [app.front.view.workspace.ecm-sync-on-finished :as ecm-sync-on-finished]
            [app.front.view.workspace.package :as package]
            [app.front.view.workspace.subs :as ws-subs]
            [app.rich-text :as rich-text]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]))

(defn reason-input [{:keys [on-change tr tr-key]}]
  [ui/text-area {:label-text (tr [tr-key])
                 :required   true
                 :on-change  #(on-change {:reason (futil/tval %)})}])

(defn rich-text-reason-input [{:keys [on-change tr tr-key]}]
  [ui-rt/editor {:id         (str "reason-" (name tr-key))
                 :label-text (tr [tr-key])
                 :required?  true
                 :on-change  #(on-change {:reason %})}])

(defmulti ^{:arglists '([{:keys [signal tr on-change parameters process-target]} signal])}
  parameter-view (fn [{:keys                                                     [signal]}] signal))

(defmethod parameter-view :default [_] nil)

(def modal-content-container :div.flex.flex-col.gap-4)

(defn- pre-consultation-conditions-confirm
  [{:keys [tr on-change parameters process-target]}]
  (let [process-target-id (if (number? process-target) process-target (:db/id process-target))
        conditions        (<sub [::condition-common/pre-consultation-origin-conditions process-target-id])]
    (when (and (<sub [::ff/enabled? :feature-flag.id/pre-consultation-conditions])
               (seq conditions))
      [:div.p-4.bg-yellow-50.border-l-4.border-status-warning
       [:p.mb-4 (tr [:condition/pre-consultation-approval-help])]
       (cc/checkbox {:id         "move-pre-consultation-conditions"
                     :label-text (tr [:condition/move-pre-consultation-conditions])
                     :checked    (true? (:move-pre-consultation-conditions? parameters))
                     :on-change  #(on-change {:move-pre-consultation-conditions? (boolean %)})})])))

(defmethod parameter-view :process.signal/withdraw-application
  [props]
  [reason-input (assoc props :tr-key :application/withdrawal-reason)])

(defmethod parameter-view :process.signal/cancel-withdrawal
  [props]
  [reason-input (assoc props :tr-key :application/cancel-withdrawal-reason)])

(defmethod parameter-view :process.signal/reject-application
  [props]
  [modal-content-container
   [reason-input (assoc props :tr-key :application/rejection-reason)]
   [ecm-sync-on-finished/ecm-sync-confirm props]])

(defmethod parameter-view :process.signal/withdraw-pre-consultation
  [props]
  [reason-input (assoc props :tr-key :application/withdrawal-reason)])

(defmethod parameter-view :process.signal/cancel-pre-consultation-withdrawal
  [props]
  [reason-input (assoc props :tr-key :application/cancel-withdrawal-reason)])

(defmethod parameter-view :process.signal/reject-pre-consultation
  [props]
  [reason-input (assoc props :tr-key :application/rejection-reason)])

(defmethod parameter-view :process.signal/revoke-licence
  [props]
  [reason-input (assoc props :tr-key :licence/revocation-reason)])

(defmethod parameter-view :process.signal/revoke-contractor-registration
  [props]
  [reason-input (assoc props :tr-key :licence/revocation-reason)])

(defmethod parameter-view :process.signal/refuse-application
  [props]
  [modal-content-container
   [reason-input (assoc props :tr-key :application/refusal-reason)]
   [ecm-sync-on-finished/ecm-sync-confirm props]])

(defmethod parameter-view :process.signal/deny-application
  [props]
  [modal-content-container
   [rich-text-reason-input (assoc props :tr-key :application/denial-reason)]
   [ecm-sync-on-finished/ecm-sync-confirm props]])

(defmethod parameter-view :process.signal/revert-to-submitted
  [{:keys [tr on-change parameters] :as props}]
  [modal-content-container
   [rich-text-reason-input (assoc props :tr-key :application/revert-to-submitted-reason)]
   ;; Processing times can only be reset when the authority group actually tracks them
   (when (<sub [::ws-subs/has-feature? :group.feature/target-processing-times])
     (cc/checkbox {:id         "reset-processing-times"
                   :label-text (tr [:application/reset-processing-times])
                   :checked    (true? (:reset-processing-times? parameters))
                   :on-change  #(on-change {:reset-processing-times? (boolean %)})}))])

(defmethod parameter-view :process.signal/delete
  [{:keys [tr on-change]}]
  [:<>
   (cc/checkbox {:id         "confirm-delete-application"
                 :label-text (tr [:delete.application/confirm])
                 :on-change  #(on-change {:confirmed? (boolean %)})})])

(defmethod parameter-view :process.signal/apply-partial-occupancy
  [props]
  [:<>
   [partial-occupancy/application-view props]])

(defmethod parameter-view :process.signal/set-finished
  [props]
  [archival-on-finished/confirm-content props])

(defmethod parameter-view :process.signal/set-appealed
  [props]
  [set-appealed-modal/set-appealed-modal props])

(defmethod parameter-view :process.signal/link-existing-workspace
  [props]
  [add-related/add-related-modal props])

(defmethod parameter-view :process.signal/create-admin-package
  [props]
  [package/create-package-modal props])

(defmethod parameter-view :process.signal/approve
  [props]
  [modal-content-container
   [pre-consultation-conditions-confirm props]
   [ecm-sync-on-finished/ecm-sync-confirm props]])

(defmethod parameter-view :process.signal/approve-in-principle
  [props]
  [modal-content-container
   [pre-consultation-conditions-confirm props]
   [ecm-sync-on-finished/ecm-sync-confirm props]])

(defmethod parameter-view :process.signal/revoke
  [props]
  [ecm-sync-on-finished/ecm-sync-confirm props])

(defmethod parameter-view :process.signal/accept-withdrawal
  [props]
  [modal-content-container
   [reason-input (assoc props :tr-key :application/accept-withdrawal-reason)]
   [ecm-sync-on-finished/ecm-sync-confirm props]])

(defmethod parameter-view :process.signal/set-closed
  [props]
  [ecm-sync-on-finished/ecm-sync-confirm props])

(defmethod parameter-view :process.signal/set-closed-without-inspection
  [props]
  [ecm-sync-on-finished/ecm-sync-confirm props])

(defmethod parameter-view :process.signal/set-dormant
  [props]
  [ecm-sync-on-finished/ecm-sync-confirm props])

(defmethod parameter-view :process.signal/set-inspection-workspace-closed
  [props]
  [reason-input (assoc props :tr-key :execution/closing-reason)])

(defmethod parameter-view :process.signal/set-work-order-as-blocked
  [props]
  [reason-input (assoc props :tr-key :execution/blocked-reason)])

(defmethod parameter-view :process.signal/set-lapsed
  [props]
  [rich-text-reason-input (assoc props :tr-key :execution/lapsed-reason)])

(defmethod parameter-view :process.signal/set-lapsed-overdue-conditions
  [props]
  [rich-text-reason-input (assoc props :tr-key :execution/lapsed-reason)])

(defmethod parameter-view :process.signal/delete-inspection-workspace
  [{:keys [tr on-change]}]
  [:<>
   (cc/checkbox {:id         "confirm-delete-inspection-workspace"
                 :label-text (tr [:delete.inspection-workspace/confirm])
                 :on-change  #(on-change {:confirmed? (boolean %)})})])

(defmulti ^{:arglists '([signal-key parameters])} valid? (fn [signal _] signal))
(defmethod valid? :default [_ _] true)

(defn has-reason? [params]
  (-> params :reason str/blank? not))

(defmethod valid? :process.signal/set-lapsed
  [_ {:keys [reason]}]
  (not (rich-text/empty-html-rich-text? reason)))

(defmethod valid? :process.signal/set-lapsed-overdue-conditions
  [_ {:keys [reason]}]
  (not (rich-text/empty-html-rich-text? reason)))

(defmethod valid? :process.signal/withdraw-application
  [_ params]
  (has-reason? params))

(defmethod valid? :process.signal/cancel-withdrawal
  [_ params]
  (has-reason? params))

(defmethod valid? :process.signal/accept-withdrawal
  [_ params]
  (has-reason? params))

(defmethod valid? :process.signal/withdraw-pre-consultation
  [_ params]
  (has-reason? params))

(defmethod valid? :process.signal/reject-application
  [_ params]
  (has-reason? params))

(defmethod valid? :process.signal/reject-pre-consultation
  [_ params]
  (has-reason? params))

(defmethod valid? :process.signal/revoke-licence
  [_ params]
  (has-reason? params))

(defmethod valid? :process.signal/revoke-contractor-registration
  [_ params]
  (has-reason? params))

(defmethod valid? :process.signal/refuse-application
  [_ params]
  (has-reason? params))

(defmethod valid? :process.signal/deny-application
  [_ {:keys [reason]}]
  ;; The denial reason is rich text, so an "empty" editor still yields markup.
  (not (rich-text/empty-html-rich-text? reason)))

(defmethod valid? :process.signal/revert-to-submitted
  [_ {:keys [reason]}]
  ;; The reason is rich text, so an "empty" editor still yields markup.
  (not (rich-text/empty-html-rich-text? reason)))

(defmethod valid? :process.signal/delete
  [_ {:keys [confirmed?]}]
  (true? confirmed?))

(defmethod valid? :process.signal/apply-partial-occupancy
  [_ _]
  (<sub [::partial-occupancy/new-application-valid?]))

(defmethod valid? :process.signal/set-finished
  [_ params]
  (archival-on-finished/valid-confirm-signal-parameters? params))

(defmethod valid? :process.signal/set-appealed
  [_ props]
  (set-appealed-modal/parameters-valid? props))

(defmethod valid? :process.signal/link-existing-workspace
  [_ props]
  (add-related/parameters-valid? props))

(defmethod valid? :process.signal/create-admin-package
  [_ props]
  (package/parameters-valid? props))

(defn- pre-consultation-conditions-confirmed? [params process-target-id]
  (or (not (<sub [::ff/enabled? :feature-flag.id/pre-consultation-conditions]))
      (empty? (<sub [::condition-common/pre-consultation-origin-conditions process-target-id]))
      (true? (:move-pre-consultation-conditions? params))))

(defmethod valid? :process.signal/approve
  [_ params]
  (let [process-target (<sub [:app.front.view.workspace.primary-actions/process-target])]
    (pre-consultation-conditions-confirmed? params (if (number? process-target) process-target (:db/id process-target)))))

(defmethod valid? :process.signal/approve-in-principle
  [_ params]
  (let [process-target (<sub [:app.front.view.workspace.primary-actions/process-target])]
    (pre-consultation-conditions-confirmed? params (if (number? process-target) process-target (:db/id process-target)))))

(defmethod valid? :process.signal/set-inspection-workspace-closed
  [_ params]
  (has-reason? params))

(defmethod valid? :process.signal/delete-inspection-workspace
  [_ {:keys [confirmed?]}]
  (true? confirmed?))

(defmethod valid? :process.signal/set-work-order-as-blocked
  [_ params]
  (has-reason? params))
